import Phaser from 'phaser';

// CameraController centralizes camera focus and zoom behavior.
// - Always follows a visible focus marker (small red, semi-transparent square)
// - Starts fully framing the entire battlefield
// - On each boundary "shrinking" cycle (from GameManager), zooms in by +5% over 3s
// - When <= 5 players left, focuses on action center; when 1 left, focuses on winner

export class CameraController {
  constructor(scene, worldCamera) {
    this.scene = scene;
    this.worldCamera = worldCamera;

    // Focus marker (world object)
    this.focusMarker = null;
    this.crosshairH = null;
    this.crosshairV = null;
    // World center marker (debug) - blue
    this.centerMarker = null;
    this.centerCrossH = null;
    this.centerCrossV = null;
    this.focusX = 0;
    this.focusY = 0;
    this.markerSizePx = 18; // desired on-screen size in pixels

    // Battlefield (world units) - always 4096x4096
    this.worldWidth = 4096;
    this.worldHeight = 4096;

    // Zoom animation state
    this.zoomAnim = null; // { startZoom, targetZoom, elapsed, duration }
    this.maxZoom = 1.1;   // sensible max
    this.minZoom = 0.05;  // sensible min

    // Phase detection for boundary shrinking
    this.prevBoundaryState = 'pausing';

    // Smoothing
    this.panLerp = 0.1;   // camera center smoothing

    // Debug throttling
    this._debugInitLogged = false;
    this._lastDebugTs = 0;

    // Manual focus experiment
    this.manualFocusActive = false;
    this.manualFocusLastTs = 0;
    this.focusTween = null;

    // Manual zoom experiment
    this.manualZoomActive = false;
    this.manualZoomLastTs = 0;
    this.manualZoomBaseline = null;
    this.zoomRevertTween = null;

    // Extra zoom triggers when player count is low
    this.minPlayersZoomThreshold = 9;
    this.lastBoostAt = 0;
    this.boostCooldownMs = 3000;
    
    // Dynamic zoom to keep final 20 players in frame
    this.dynamicZoomThreshold = 20;
    this.lastDynamicZoomCheck = 0;
    this.dynamicZoomCheckInterval = 500; // Check every 500ms
  }

  init(/* battlefieldSize parameter ignored - always 4096x4096 */) {
    // Battlefield is always 4096x4096 (hardcoded in constructor)
    // battlefieldSize parameter is ignored for simplicity

    // Create focus marker (red translucent square)
    if (!this.focusMarker || !this.focusMarker.active) {
      this.focusMarker = this.scene.add.rectangle(0, 0, this.markerSizePx, this.markerSizePx, 0xFF0000, 0.1);
      this.focusMarker.setDepth(999);
      if (this.focusMarker.setStrokeStyle) this.focusMarker.setStrokeStyle(1, 0xffffff, 0.1);
      this.focusMarker.setBlendMode(Phaser.BlendModes.ADD);

      // Add subtle crosshair for easier visibility
      const len = this.markerSizePx * 1.5;
      this.crosshairH = this.scene.add.line(0, 0, -len, 0, len, 0, 0xffffff, 0.1);
      this.crosshairV = this.scene.add.line(0, 0, 0, -len, 0, len, 0xffffff, 0.1);
      [this.crosshairH, this.crosshairV].forEach(l => {
        l.setDepth(998);
        l.setBlendMode(Phaser.BlendModes.ADD);
      });

      // Ensure UI camera does not render the world focus visuals
      if (this.scene.uiCamera && this.scene.uiCamera.ignore) {
        this.scene.uiCamera.ignore([this.focusMarker, this.crosshairH, this.crosshairV]);
      }
    }

    // Initial focus – prefer scene-provided values (e.g., spawn center)
    console.log('[CameraController] init - checking initialFocusX/Y:', {
      hasInitialFocusX: typeof this.scene?.initialFocusX === 'number',
      hasInitialFocusY: typeof this.scene?.initialFocusY === 'number',
      initialFocusX: this.scene?.initialFocusX,
      initialFocusY: this.scene?.initialFocusY
    });
    
    if (typeof this.scene?.initialFocusX === 'number' && typeof this.scene?.initialFocusY === 'number') {
      this.focusX = this.scene.initialFocusX;
      this.focusY = this.scene.initialFocusY;
      console.log('[CameraController] Set focus from scene initialFocusX/Y:', {
        focusX: this.focusX,
        focusY: this.focusY
      });
    } else {
      this.setFullFocus();
      console.log('[CameraController] Used setFullFocus(), focusX/Y:', {
        focusX: this.focusX,
        focusY: this.focusY
      });
    }

    // Initial zoom
    const initialZoom = this.scene?.initialWorldZoom || 0.45;
    this.worldCamera.setZoom(initialZoom);
    
    // Calculate padding so camera can view battlefield edges
    // Phaser camera bounds constrain the camera CENTER, not the viewport
    // To show (0,0) at top-left, camera center must be at least at (viewport.width/2/zoom, viewport.height/2/zoom)
    // We need enough negative padding to allow camera center to move up/left enough
    
    // Use viewport size at minimum zoom (largest viewport) + current zoom for conservative padding
    const viewportWorldW = this.worldCamera.width / initialZoom;
    const viewportWorldH = this.worldCamera.height / initialZoom;
    const viewportWorldWAtMinZoom = this.worldCamera.width / this.minZoom;
    const viewportWorldHAtMinZoom = this.worldCamera.height / this.minZoom;
    
    // Padding must be at least half viewport size + extra buffer
    // Use the larger of current zoom or min zoom to ensure we always have enough
    // Extra padding for top (Y) to ensure camera can show (0,0) with visible padding above
    const paddingX = Math.max(viewportWorldW / 2, viewportWorldWAtMinZoom / 2) + 200;
    const paddingY = Math.max(viewportWorldH / 2, viewportWorldHAtMinZoom / 2) + 800; // Large padding for top edge (creates black space above battlefield)
    this.cameraPadding = { x: paddingX, y: paddingY };
    
    console.log('[CameraController] Camera bounds calculation:', {
      viewportWorldW,
      viewportWorldH,
      viewportWorldWAtMinZoom,
      viewportWorldHAtMinZoom,
      paddingX,
      paddingY,
      bounds: {
        x: -paddingX,
        y: -paddingY,
        width: this.worldWidth + paddingX * 2,
        height: this.worldHeight + paddingY * 2
      }
    });
    
    // Set camera bounds: camera center can move from (-padding) to (4096 + padding)
    // Negative Y bounds create black space above battlefield, allowing camera to show top edge
    // This allows viewport to show from (0,0) to (4096,4096) with padding above
    try {
      this.worldCamera.setBounds(-paddingX, -paddingY, this.worldWidth + paddingX * 2, this.worldHeight + paddingY * 2);
      console.log('[CameraController] setBounds successful, re-centering camera on focus');
      // Re-center camera after bounds are set to ensure it can reach the focus position
      this.centerCameraImmediate();
    } catch (error) {
      console.warn('CameraController: Error setting initial camera bounds:', error);
      // Try to set safe default bounds
      try {
        this.worldCamera.setBounds(0, 0, 4096, 4096);
        this.centerCameraImmediate();
      } catch (fallbackError) {
        console.error('CameraController: Failed to set fallback initial bounds:', fallbackError);
      }
    }

    if (!this._debugInitLogged) {
      const zoomValue = this.scene?.initialWorldZoom || 0.45;
      console.log(`[CamFocus] init: world ${this.worldWidth}x${this.worldHeight}, initialZoom=${zoomValue.toFixed(3)}, focus=(${this.focusX.toFixed(1)},${this.focusY.toFixed(1)})`);
      //console.log('[CamFocus] visuals created:', !!this.focusMarker, !!this.crosshairH, !!this.crosshairV);
      this._debugInitLogged = true;
    }

    // Input handlers for manual focus and zoom
    this._setupInputHandlers();
    // Ensure click zone matches world size
    this._ensureClickZone();
    // Create/update center marker at true world midpoint
    this._ensureCenterMarker();
  }

  _setupInputHandlers() {
    // Global pointer down log (scene-level)
    this.scene.input.on('pointerdown', (pointer) => {
      const world = this._screenToWorld(pointer.x, pointer.y);
      //console.log(`[CamFocus] scene.pointerdown screen=(${pointer.x.toFixed(1)},${pointer.y.toFixed(1)}) world=(${world.x.toFixed(1)},${world.y.toFixed(1)})`);
    });

    // Ensure click zone exists/updated
    this._ensureClickZone();

    // Wheel to manual zoom
    this.scene.input.on('wheel', (_pointer, _go, _dx, dy) => {
      const current = this.worldCamera.zoom || 1;
      // dy > 0 => scroll down -> zoom out
      const factor = Math.pow(1.0015, -dy);
      const maxZ = this._computeMaxZoom();
      const next = Phaser.Math.Clamp(current * factor, this.minZoom, maxZ);
      if (this.zoomRevertTween) {
        this.zoomRevertTween.stop();
        this.zoomRevertTween = null;
      }
      if (!this.manualZoomActive) {
        this.manualZoomBaseline = current; // capture once at session start
      }
      this.manualZoomActive = true;
      this.manualZoomLastTs = this.scene.time.now || Date.now();
      this.worldCamera.setZoom(next);
      //console.log(`[CamFocus] wheel zoom ${current.toFixed(3)} -> ${next.toFixed(3)}`);
    });
  }

  _ensureClickZone() {
    if (!this.scene) return;
    if (!this.clickZone || !this.clickZone.active) {
      this.clickZone = this.scene.add.zone(this.worldWidth / 2, this.worldHeight / 2, this.worldWidth, this.worldHeight)
        .setOrigin(0.5, 0.5)
        .setInteractive({ useHandCursor: false })
        .setScrollFactor(1)
        .setDepth(-50);
      if (this.scene.uiCamera && this.scene.uiCamera.ignore) this.scene.uiCamera.ignore(this.clickZone);
      this.clickZone.on('pointerdown', (pointer) => {
        const world = this._screenToWorld(pointer.x, pointer.y);
        const cx = Phaser.Math.Clamp(world.x, 0, this.worldWidth);
        const cy = Phaser.Math.Clamp(world.y, 0, this.worldHeight);
        //console.log(`[CamFocus] zone.pointerdown -> tween to (${cx.toFixed(1)},${cy.toFixed(1)})`);
        this._startFocusTweenTo(cx, cy, 2000);
        this.manualFocusActive = true;
        this.manualFocusLastTs = this.scene.time.now || Date.now();
      });
    } else {
      this.clickZone.setPosition(this.worldWidth / 2, this.worldHeight / 2);
      this.clickZone.setSize(this.worldWidth, this.worldHeight);
    }
  }

  _ensureCenterMarker() {
    const cx = this.worldWidth / 2;
    const cy = this.worldHeight / 2;
    if (!this.centerMarker || !this.centerMarker.active) {
      this.centerMarker = this.scene.add.rectangle(cx, cy, this.markerSizePx, this.markerSizePx, 0x2e6cff, 0.01);
      this.centerMarker.setDepth(995);
      if (this.centerMarker.setStrokeStyle) this.centerMarker.setStrokeStyle(1, 0xffffff, 0.01);
      this.centerMarker.setBlendMode(Phaser.BlendModes.ADD);
      const len = this.markerSizePx * 1.4;
      /*this.centerCrossH = this.scene.add.line(cx, cy, -len, 0, len, 0, 0x2e6cff, 0.6).setDepth(994).setBlendMode(Phaser.BlendModes.ADD);
      this.centerCrossV = this.scene.add.line(cx, cy, 0, -len, 0, len, 0x2e6cff, 0.6).setDepth(994).setBlendMode(Phaser.BlendModes.ADD);*/
      if (this.scene.uiCamera && this.scene.uiCamera.ignore) this.scene.uiCamera.ignore([this.centerMarker/*, this.centerCrossH, this.centerCrossV*/]);
    } else {
      this.centerMarker.setPosition(cx, cy);
      /*this.centerCrossH.setPosition(cx, cy);
      this.centerCrossV.setPosition(cx, cy);*/
    }
  }

  _screenToWorld(screenX, screenY) {
    // Convert from page/screen coords used by Phaser pointer to world coords
    // pointer.x/y are relative to the game canvas (already adjusted by scale manager)
    const cam = this.worldCamera;
    const wx = cam.worldView.x + screenX / cam.zoom;
    const wy = cam.worldView.y + screenY / cam.zoom;
    return { x: wx, y: wy };
  }

  _startFocusTweenTo(x, y, duration = 2000) {
    if (this.focusTween) {
      this.focusTween.stop();
      this.focusTween = null;
    }
    // Tween the controller's focusX/Y so the camera centers via update()
    this.focusTween = this.scene.tweens.add({
      targets: this,
      focusX: x,
      focusY: y,
      duration,
      ease: 'Sine.easeInOut',
      onUpdate: () => {
        // Keep visuals in sync during tween
        if (this.focusMarker && this.focusMarker.active) this.focusMarker.setPosition(this.focusX, this.focusY);
        /*if (this.crosshairH && this.crosshairV) {
          this.crosshairH.setPosition(this.focusX, this.focusY);
          this.crosshairV.setPosition(this.focusX, this.focusY);
        }*/
      },
      onComplete: () => { this.focusTween = null; }
    });
  }

  // External API to set focus modes
  setFullFocus() {
    this.focusX = this.worldWidth / 2;
    this.focusY = this.worldHeight / 2;
  }

  setActionTargets(players) {
    if (!players || players.length === 0) return;
    let sumX = 0, sumY = 0;
    players.forEach(p => { sumX += p.sprite.x; sumY += p.sprite.y; });
    this.focusX = sumX / players.length;
    this.focusY = sumY / players.length;
  }

  setPlayerTarget(player) {
    if (!player || !player.sprite) return;
    this.focusX = player.sprite.x;
    this.focusY = player.sprite.y;
  }

  // Trigger a ~+8% zoom-in over ~2 seconds from current camera zoom
  startShrinkZoom() {
    const start = this.worldCamera.zoom;
    const target = Math.min(this.maxZoom, start * 1.08);
    this.zoomAnim = { startZoom: start, targetZoom: target, elapsed: 0, duration: 2000 };
  }

  // Additional zoom boost to keep action in frame when players are low
  startBoostZoom(multiplier = 1.04, durationMs = 3200) {
    const start = this.worldCamera.zoom;
    const target = Math.min(this.maxZoom, start * multiplier);
    this.zoomAnim = { startZoom: start, targetZoom: target, elapsed: 0, duration: durationMs };
  }

  centerCameraImmediate() {
    if (!this.worldCamera) {
      return;
    }

    // Ensure bounds exist before attempting to center. Phaser's Camera.centerOn
    // will attempt to clamp using its internal _bounds rect, which can be null
    // if setBounds has never succeeded or bounds were cleared.
    if (!this.worldCamera._bounds || this.worldCamera._bounds.width === 0) {
      const padX = this.cameraPadding?.x ?? 0;
      const padY = this.cameraPadding?.y ?? 0;
      try {
        this.worldCamera.setBounds(
          -padX,
          -padY,
          this.worldWidth + padX * 2,
          this.worldHeight + padY * 2
        );
      } catch (_) {
        // If bounds still cannot be set (e.g. camera not ready yet), bail out safely.
        return;
      }
    }

    if (!this.worldCamera.worldView) {
      return;
    }

    const beforeCenter = this.worldCamera.worldView ? {
      centerX: this.worldCamera.worldView.centerX,
      centerY: this.worldCamera.worldView.centerY
    } : null;
    
    /*console.log('[CameraController] centerCameraImmediate - BEFORE:', {
      focusX: this.focusX,
      focusY: this.focusY,
      cameraCenterBefore: beforeCenter,
      cameraBounds: this.worldCamera.getBounds ? this.worldCamera.getBounds() : 'no getBounds method'
    });*/
    
    this.worldCamera.centerOn(this.focusX, this.focusY);
    
    const afterCenter = this.worldCamera.worldView ? {
      centerX: this.worldCamera.worldView.centerX,
      centerY: this.worldCamera.worldView.centerY
    } : null;
    
    if (this.focusMarker) {
      this.focusMarker.setPosition(this.focusX, this.focusY);
    }
    
    /*console.log('[CameraController] centerCameraImmediate - AFTER:', {
      focusX: this.focusX,
      focusY: this.focusY,
      cameraCenterAfter: afterCenter,
      markerPosition: this.focusMarker ? { x: this.focusMarker.x, y: this.focusMarker.y } : 'no marker',
      centerOnWorked: afterCenter && Math.abs(afterCenter.centerX - this.focusX) < 1 && Math.abs(afterCenter.centerY - this.focusY) < 1
    });*/
  }

  update(delta, boundaryState = 'pausing') {
    // Early return if worldCamera is not available or in invalid state
    if (!this.worldCamera || !this.worldCamera.worldView || !this.worldCamera.worldView.setTo) {
      console.warn('CameraController: worldCamera is null or in invalid state, skipping update');
      return;
    }
    
    const maxZDynamic = this._computeMaxZoom();
    // Detect boundary transition to "shrinking" to kick off the 5% zoom-in
    if (this.prevBoundaryState !== 'shrinking' && boundaryState === 'shrinking') {
      this.startShrinkZoom();
    }
    this.prevBoundaryState = boundaryState;

    // Smoothly move focus marker to target and keep a constant on-screen size
    if (this.focusMarker && this.focusMarker.active) {
      // If a focus tween is running, visuals are updated in onUpdate
      if (!this.focusTween) {
        const fx = Phaser.Math.Linear(this.focusMarker.x, this.focusX, this.panLerp);
        const fy = Phaser.Math.Linear(this.focusMarker.y, this.focusY, this.panLerp);
        this.focusMarker.setPosition(fx, fy);
        /*if (this.crosshairH && this.crosshairV) {
          this.crosshairH.setPosition(fx, fy);
          this.crosshairV.setPosition(fx, fy);
        }*/
      }
      const z = this.worldCamera.zoom || 1;
      const inv = 1 / z;
      // Keep visual size roughly constant in screen space
      this.focusMarker.setScale(inv, inv);
      if (this.crosshairH && this.crosshairV) {
        this.crosshairH.setScale(inv, inv);
        this.crosshairV.setScale(inv, inv);
      }
      // Scale center marker similarly
      if (this.centerMarker) {
        this.centerMarker.setScale(inv, inv);
        if (this.centerCrossH && this.centerCrossV) {
          this.centerCrossH.setScale(inv, inv);
          this.centerCrossV.setScale(inv, inv);
        }
      }
    }

    // Smoothly center camera on focus
    // Bounds are set once in init() and never change
    const currentCenterX = this.worldCamera.worldView.centerX;
    const currentCenterY = this.worldCamera.worldView.centerY;
    const newCenterX = Phaser.Math.Linear(currentCenterX, this.focusX, this.panLerp);
    const newCenterY = Phaser.Math.Linear(currentCenterY, this.focusY, this.panLerp);
    this.worldCamera.centerOn(newCenterX, newCenterY);

    // Run zoom animation if active
    if (this.zoomAnim) {
      this.zoomAnim.elapsed += delta;
      const t = Math.min(1, this.zoomAnim.elapsed / this.zoomAnim.duration);
      const eased = Phaser.Math.Easing.Quadratic.InOut(t);
      const z = Phaser.Math.Linear(this.zoomAnim.startZoom, this.zoomAnim.targetZoom, eased);
      const clamped = Phaser.Math.Clamp(z, this.minZoom, maxZDynamic);
      this.worldCamera.setZoom(clamped);
      if (t >= 1) this.zoomAnim = null;
    }

    // Dynamic zoom to keep final 20 players in frame
    /*try {
      const alivePlayers = (this.scene.players || []).filter(p => p && p.isAlive);
      const nowTs2 = this.scene.time?.now || Date.now();
      
      if (alivePlayers.length > 0 && alivePlayers.length <= this.dynamicZoomThreshold) {
        // Check periodically to adjust zoom based on player spread
        if (nowTs2 - this.lastDynamicZoomCheck > this.dynamicZoomCheckInterval) {
          this.lastDynamicZoomCheck = nowTs2;
          this._adjustZoomForPlayers(alivePlayers);
        }
      } else if (alivePlayers.length > 0 && alivePlayers.length < this.minPlayersZoomThreshold) {
        // Original zoom boost logic for very low player counts
        if (!this.zoomAnim && (nowTs2 - this.lastBoostAt > this.boostCooldownMs)) {
          this.startBoostZoom(1.06, 1600);
          this.lastBoostAt = nowTs2;
        }
      }
    } catch (_) {}*/

    // Dynamic zoom to keep final 20 players in frame
    try {
      const alivePlayers = (this.scene.players || []).filter(p => p && p.isAlive);
      const nowTs2 = this.scene.time?.now || Date.now();
      
      // Don't run automatic zoom adjustments if user is actively manually zooming
      if (!this.manualZoomActive) {
        if (alivePlayers.length > 0 && alivePlayers.length <= this.dynamicZoomThreshold) {
          // Check periodically to adjust zoom based on player spread
          if (nowTs2 - this.lastDynamicZoomCheck > this.dynamicZoomCheckInterval) {
            this.lastDynamicZoomCheck = nowTs2;
            this._adjustZoomForPlayers(alivePlayers);
          }
        } else if (alivePlayers.length > 0 && alivePlayers.length < this.minPlayersZoomThreshold) {
          // Original zoom boost logic for very low player counts
          if (!this.zoomAnim && (nowTs2 - this.lastBoostAt > this.boostCooldownMs)) {
            this.startBoostZoom(1.06, 1600);
            this.lastBoostAt = nowTs2;
          }
        }
      }
    } catch (_) {}

    // Manual focus re-center after inactivity (20s)
    const nowTs = this.scene.time?.now || Date.now();
    if (this.manualFocusActive && nowTs - this.manualFocusLastTs > 20000) {
      this.manualFocusActive = false;
      this._startFocusTweenTo(this.worldWidth / 2, this.worldHeight / 2, 2000);
      //console.log('[CamFocus] focus timeout -> tween back to center');
    }

    // Manual zoom revert after inactivity (20s)
    if (this.manualZoomActive && nowTs - this.manualZoomLastTs > 20000) {
      this.manualZoomActive = false;
      const baseline = (typeof this.manualZoomBaseline === 'number') ? this.manualZoomBaseline : this.worldCamera.zoom;
      this.zoomRevertTween = this.scene.tweens.add({
        targets: this.worldCamera,
        zoom: Phaser.Math.Clamp(baseline, this.minZoom, this._computeMaxZoom()),
        duration: 1000,
        ease: 'Sine.easeInOut',
        onComplete: () => { this.zoomRevertTween = null; }
      });
      //console.log('[CamFocus] zoom timeout -> revert to baseline');
    }

    // Periodic debug (1/sec)
    const now = this.scene.time?.now || 0;
    if (now - this._lastDebugTs > 1000) {
      const view = this.worldCamera.worldView;
      const inView = view.contains(this.focusX, this.focusY);
      //console.log(`[CamFocus] update: pos=(${this.focusX.toFixed(1)},${this.focusY.toFixed(1)}), camCenter=(${view.centerX.toFixed(1)},${view.centerY.toFixed(1)}), zoom=${(this.worldCamera.zoom||1).toFixed(3)}, inView=${inView}`);
      this._lastDebugTs = now;
    }
  }

  _computeMaxZoom() {
    try {
      const alive = (this.scene.players || []).filter(p => p && p.isAlive).length;
      if (alive > 20) return Math.min(this.maxZoom, 0.7);
      return this.maxZoom; // Return maxZoom (1.1) for 20 or fewer players
    } catch (_) {
      return this.maxZoom;
    }
  }

  _adjustZoomForPlayers(alivePlayers) {
    if (!alivePlayers || alivePlayers.length === 0) return;

    // Calculate bounding box for all alive players
    let minX = Infinity, maxX = -Infinity;
    let minY = Infinity, maxY = -Infinity;

    alivePlayers.forEach(p => {
      if (p.sprite) {
        minX = Math.min(minX, p.sprite.x);
        maxX = Math.max(maxX, p.sprite.x);
        minY = Math.min(minY, p.sprite.y);
        maxY = Math.max(maxY, p.sprite.y);
      }
    });

    // Add padding around the bounding box
    const padding = 200;
    const playersWidth = (maxX - minX) + padding * 2;
    const playersHeight = (maxY - minY) + padding * 2;

    // Calculate required zoom to fit all players
    const cameraWidth = this.worldCamera.width;
    const cameraHeight = this.worldCamera.height;
    
    const zoomX = cameraWidth / playersWidth;
    const zoomY = cameraHeight / playersHeight;
    
    // Use the smaller zoom to ensure both dimensions fit
    let requiredZoom = Math.min(zoomX, zoomY);
    
    // Clamp to valid zoom range - don't zoom in closer than maxZoom
    requiredZoom = Phaser.Math.Clamp(requiredZoom, this.minZoom, this.maxZoom);
    
    const currentZoom = this.worldCamera.zoom || 1;
    
    // Only adjust if the difference is significant (more than 5%)
    if (Math.abs(requiredZoom - currentZoom) / currentZoom > 0.05) {
      // Smoothly animate to the required zoom
      if (!this.zoomAnim) {
        this.zoomAnim = { 
          startZoom: currentZoom, 
          targetZoom: requiredZoom, 
          elapsed: 0, 
          duration: 1000 
        };
      }
    }
  }
}

export default CameraController;


